import styles from "../../styles/CoordinationBoards.module.css"
import { useContext, useState, useEffect, useRef, useCallback } from "react"
import { useQuery } from "@apollo/client"
import { UserContext } from "../../context/userContext"
import axios from "axios"
import { coordinationBoardsApiUrl } from "../../config/microservicesUrls"
import { LoaderMedium } from "../../components/Loaders"
import { Toast } from "primereact/toast"
import { Tag } from "primereact/tag"
import { MultiSelect } from "primereact/multiselect"
import {
  TopicStatus,
  Actions,
  BoardType,
  Ascending,
  TopicStatusChange,
} from "./Types/Enums"
import { Calendar } from "primereact/calendar"
import { Button } from "primereact/button"
import { Accordion, AccordionTab } from "primereact/accordion"
import { TabView, TabPanel } from "primereact/tabview"
import {
  showError,
  GET_STAFF_NAMES,
  GET_PARTICIPANTS_BY_ENTERPRISE_ID,
  GET_PARTICIPANTS_BY_ORG_LIST,
  updateAndMoveTopic,
  renderTag,
  mapTopicTableFields,
  GET_USERS_BY_ENTERPRISE_ID,
  GET_USERS_BY_ORG_LIST,
  findName,
} from "./Utils/Utils"
import TopicForm from "./TopicForm"
import { InputText } from "primereact/inputtext"
import TopicView from "./Topic"
import NoData from "./Utils/NoData"
import { Dropdown } from "primereact/dropdown"
import { Paginator } from "primereact/paginator"
import { LogError } from "../../helpers/logger"
import { DateTime } from "luxon"

const api_url = coordinationBoardsApiUrl()
const pageSizeOptions = [10, 25, 50, 100]
const defaultPageSize = pageSizeOptions[0]

const Board = ({ board, closeView, defaultTags, selectedTopicId }) => {
  const userCtx = useContext(UserContext)
  const [loading, setLoading] = useState(false)
  const [loadingSmall, setLoadingSmall] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const toast = useRef(null)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [showTopicForm, setShowTopicForm] = useState(false)
  const [showTopicView, setShowTopicView] = useState(false)
  const [topic, setTopic] = useState(null)
  const [topicAction, setTopicAction] = useState(Actions.Add)
  const [groupedParticipants, setGroupedParticipants] = useState([])
  const [staffSuggestions, setStaffSuggestions] = useState([])

  // Pagination & Filters
  const [activePageNum, setActivePageNum] = useState(1) // Active page number for pagination
  const [pageSize, setPageSize] = useState(defaultPageSize) // Number of topics per page
  const [totalTopics, setTotalTopics] = useState(defaultPageSize) // Total number of topics (for pagination)
  const [activeFilters, setActiveFilters] = useState({}) // To know if any filter is applied on active tab

  // All Active Topics list contains all active and completed
  const [allActiveTopics, setAllActiveTopics] = useState([])
  const [allArchivedTopics, setAllArchivedTopics] = useState([])

  // Filters and Sorting
  const topicSortingOptions = [
    { column: "Subject", id: "subject" },
    { column: "Created By", id: "createdBy" },
    { column: "Created On", id: "createdOn" },
    { column: "Updated/Activity By", id: "updatedBy" },
    { column: "Updated/Activity On", id: "updatedOn" },
    { column: "New", id: "new" },
  ]

  //Active
  const [activeCreatedByOptions, setActiveCreatedByOptions] = useState([])
  const [activeCreatedBy, setActiveCreatedBy] = useState([])
  const [activeCreatedOn, setActiveCreatedOn] = useState([])
  const [activeUpdatedByOptions, setActiveUpdatedByOptions] = useState([])
  const [activeUpdatedBy, setActiveUpdatedBy] = useState([])
  const [activeUpdatedOn, setActiveUpdatedOn] = useState([])
  const [activeTagOptions, setActiveTagOptions] = useState([])
  const [activeTag, setActiveTag] = useState([])
  const [activeSearchPhrase, setActiveSearchPhrase] = useState("")
  const [activeSorts, setActiveSorts] = useState(
    topicSortingOptions.map((o) => null),
  )
  const [activeSortOrders, setActiveSortOrders] = useState(
    topicSortingOptions.map((o) => Ascending),
  )

  //Archived
  const [archivedCreatedByOptions, setArchivedCreatedByOptions] = useState([])
  const [archivedCreatedBy, setArchivedCreatedBy] = useState([])
  const [archivedCreatedOn, setArchivedCreatedOn] = useState([])
  const [archivedUpdatedByOptions, setArchivedUpdatedByOptions] = useState([])
  const [archivedUpdatedBy, setArchivedUpdatedBy] = useState([])
  const [archivedUpdatedOn, setArchivedUpdatedOn] = useState([])
  const [archivedTagOptions, setArchivedTagOptions] = useState([])
  const [archivedTag, setArchivedTag] = useState([])
  const [archivedSearchPhrase, setArchivedSearchPhrase] = useState("")
  const [archivedSorts, setArchivedSorts] = useState(
    topicSortingOptions.map((o) => null),
  )
  const [archivedSortOrders, setArchivedSortOrders] = useState(
    topicSortingOptions.map((o) => Ascending),
  )

  const renderOtherSorts = (
    sortOptions,
    setSortOptions,
    sortOrders,
    setSortOrders,
  ) => {
    let items = []
    for (let i = 1; i < topicSortingOptions.length; i++) {
      items.push(
        sortOptions[i - 1] !== null && (
          <div className={styles.field} key={`sorting_${i}`}>
            <label className={styles.label}>Then by</label>
            <div style={{ display: "flex", gap: "5px", width: "100%" }}>
              <Dropdown
                value={sortOptions[i]}
                className={styles.selector}
                options={topicSortingOptions.filter(
                  (o) => !sortOptions.slice(0, i).includes(o.id),
                )}
                panelClassName="custom_dropdown_panel"
                onChange={(e) => {
                  setSortOptions(
                    sortOptions.map((o, index) => (index === i ? e.value : o)),
                  )
                }}
                optionLabel="column"
                optionValue="id"
              />
              <Button
                icon={
                  sortOrders[i] === Ascending
                    ? "pi pi-arrow-up"
                    : "pi pi-arrow-down"
                }
                className={styles.sort_btn}
                disabled={sortOptions[i] === null}
                onClick={() => {
                  setSortOrders(
                    sortOrders.map((o, index) => (index === i ? !o : o)),
                  )
                }}
              />
            </div>
          </div>
        ),
      )
    }
    return items
  }

  const { refetch } = useQuery(GET_STAFF_NAMES, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
      staff_ids: [userCtx.session.user.staff_id],
    },
    skip: true,
  })

  const { refetch: refetchParticipantsByEnterprise } = useQuery(
    GET_PARTICIPANTS_BY_ENTERPRISE_ID,
    {
      variables: {
        staff_id: userCtx.session.user.staff_id,
        staff_organization_id: userCtx.session.user.organization_id,
        enterprise_id: userCtx.session.user.enterprise_id,
      },
      skip: true,
    },
  )

  const { refetch: refetchParticipantsByOrgs } = useQuery(
    GET_PARTICIPANTS_BY_ORG_LIST,
    {
      variables: {
        staff_id: userCtx.session.user.staff_id,
        staff_organization_id: userCtx.session.user.organization_id,
        organizations_ids: [userCtx.session.user.organization_id],
      },
      skip: true,
    },
  )

  const { refetch: refetchUsersByEnterpise } = useQuery(
    GET_USERS_BY_ENTERPRISE_ID,
    {
      variables: {
        staff_id: userCtx.session.user.staff_id,
        staff_organization_id: userCtx.session.user.organization_id,
        enterprise_id: userCtx.session.user.enterprise_id,
      },
      skip: true,
    },
  )

  const { refetch: refetchUsersByOrgs } = useQuery(GET_USERS_BY_ORG_LIST, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      staff_organization_id: userCtx.session.user.organization_id,
      organizations_ids: [userCtx.session.user.organization_id],
    },
    skip: true,
  })

  const actualStaffName = {
    first: userCtx.session.user.staff_name_first,
    last: userCtx.session.user.staff_name_last,
    full_name: `${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`,
  }

  const getStaffsNames = async (staffIds) => {
    if (staffIds.length > 0) {
      const res = await refetch({
        staff_id: userCtx.session.user.staff_id,
        organization_id: userCtx.session.user.organization_id,
        staff_ids: [...new Set(staffIds)],
      })
      if (res.data.getStaffNamesByIds?.data) {
        return JSON.parse(res.data.getStaffNamesByIds.data)
      }
    }
    return {}
  }

  const canEditTopic = useCallback(
    (topicCreator) => {
      if (board.readOnly) return false
      const isAllowedToEdit = userCtx.session.user.staff_role <= 3 // org admins are the lowest roles to be able to edit
      return (
        topicCreator === userCtx.session.user.staff_id ||
        isAllowedToEdit ||
        board.statusChange === TopicStatusChange.Anyone
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [userCtx.session.user.staff_id, userCtx.session.user.staff_role],
  )

  const justStatusEdits = useCallback(
    (topicCreator) => {
      const isAllowedToEdit = userCtx.session.user.staff_role <= 3 // org admins are the lowest roles to be able to edit
      return (
        topicCreator !== userCtx.session.user.staff_id &&
        !isAllowedToEdit &&
        board.statusChange === TopicStatusChange.Anyone
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [userCtx.session.user.staff_id, userCtx.session.user.staff_role],
  )

  const fillCreatedByOptions = (
    createdByIds,
    staffNames,
    setCreatedByOptions,
  ) => {
    const createdByOptions = createdByIds
      .map((id) => {
        const user = findName(id, staffNames)
        return user.id ? { name: user.full_name, id: user.id } : null
      })
      .filter(Boolean)
    setCreatedByOptions(createdByOptions)
  }

  const fillUpdatedByOptions = (
    updatedActivityByIds,
    staffNames,
    setUpdatedByOptions,
  ) => {
    const updatedActivityByOptions = updatedActivityByIds
      .map((id) => {
        const user = findName(id, staffNames)
        return user.id ? { name: user.full_name, id: user.id } : null
      })
      .filter(Boolean)
    setUpdatedByOptions(updatedActivityByOptions)
  }

  const fillFilterOptions = (
    filters,
    staffNames,
    setCreatedByOptions,
    setUpdatedByOptions,
    setTagOptions,
  ) => {
    fillCreatedByOptions(
      filters.createdByUsers,
      staffNames,
      setCreatedByOptions,
    )
    fillUpdatedByOptions(
      filters.updatedActivityByUsers,
      staffNames,
      setUpdatedByOptions,
    )
    setTagOptions(filters.tags)
  }

  useEffect(() => {
    setLoading(true)

    // Reset page numbers
    setActivePageNum(1)

    const boardFetchDetails = buildBoardFetchQuery(true, 1) // Fetch active topics only for initial load
    let promises = [boardFetchDetails]
    let organizations_ids = []

    if (board.type === BoardType.Default) {
      organizations_ids = [board.ownerOrganizationId]
    }
    if (board.type === BoardType["Cross Organization"]) {
      organizations_ids = board.organizations
    }
    if (board.type === BoardType["Referral Partner"]) {
      organizations_ids = [board.ownerOrganizationId, board.organizations[0]]
    }

    if (board.type === BoardType.Enterprise) {
      promises.push(
        refetchParticipantsByEnterprise({
          staff_id: userCtx.session.user.staff_id,
          staff_organization_id: userCtx.session.user.organization_id,
          enterprise_id: board.ownerEnterpriseId,
        }),
      )
      promises.push(
        refetchUsersByEnterpise({
          staff_id: userCtx.session.user.staff_id,
          staff_organization_id: userCtx.session.user.organization_id,
          enterprise_id: board.ownerEnterpriseId,
        }),
      )
    } else {
      promises.push(
        refetchParticipantsByOrgs({
          staff_id: userCtx.session.user.staff_id,
          staff_organization_id: userCtx.session.user.organization_id,
          organizations_ids: organizations_ids,
        }),
      )
      promises.push(
        refetchUsersByOrgs({
          staff_id: userCtx.session.user.staff_id,
          staff_organization_id: userCtx.session.user.organization_id,
          organizations_ids: organizations_ids,
        }),
      )
    }

    Promise.all(promises)
      .then(async (results) => {
        const [boardResponse, ...remainingResponses] = results

        // Process board details
        await processBoardFetchDetailsResponse(boardResponse)

        // Process participants and users
        remainingResponses.forEach((response) => {
          let _participantsbyOrg = {}
          if (
            response.data.participantsByEnterpriseUid ||
            response.data.participantsByOrgList
          ) {
            const data =
              response.data.participantsByEnterpriseUid ||
              response.data.participantsByOrgList
            data.forEach((participant) => {
              const name = `${participant.firstName} ${participant.lastName}`
              // Add a label to be used in the dropdown
              const label = `${name} - ID ${participant.participant_alias} `
              // if organization_id not in participantsByOrg dic, add it
              if (!(participant.organization_id in _participantsbyOrg)) {
                _participantsbyOrg[participant.organization_id] = {
                  orgName: participant.organization_name,
                  participants: [
                    {
                      id: participant.participant_id,
                      name,
                      label,
                      alias: participant.participant_alias,
                    },
                  ],
                }
              } else {
                _participantsbyOrg[
                  participant.organization_id
                ].participants.push({
                  id: participant.participant_id,
                  name,
                  label,
                  alias: participant.participant_alias,
                })
              }
            })
            // Convert the object back into an array
            _participantsbyOrg = Object.values(_participantsbyOrg)
            setGroupedParticipants(_participantsbyOrg)
          }
          if (
            response.data.allUsersByEnterpriseId ||
            response.data.allUsersByOrgList
          ) {
            const data =
              response.data.allUsersByEnterpriseId ||
              response.data.allUsersByOrgList
            let _suggestions = []
            let addedOrgs = new Set()
            // map the data to the format required for mentions, which is an array of objects with id and value
            // add also the organization name as an option to the list for grouping purposes
            data.forEach((user) => {
              if (!addedOrgs.has(user.organization_id)) {
                addedOrgs.add(user.organization_id)
                _suggestions.push({
                  id: user.organization_id,
                  value: user.organization_name,
                  choosable: false,
                })
              }
              _suggestions.push({
                id: user.staff_id,
                value: user.staff_full_name,
                organizationId: user.organization_id,
                choosable: true,
              })
            })
            setStaffSuggestions(_suggestions)
          }
        })
        setLoading(false)
        setInitialized(true)
      })
      .catch((error) => {
        console.log(error)
        setLoading(false)
        const errors = error.response && error.response.data.errors
        showError(toast, "Not able to get data", errors)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board])

  const prevTabIndex = useRef(activeTabIndex)
  const prevFilters = useRef(activeFilters)

  useEffect(() => {
    if (!initialized) return

    let shouldResetPage = false

    // Reset page if tab changed
    if (prevTabIndex.current !== activeTabIndex) {
      setActivePageNum(1)
      shouldResetPage = true
    }
    // Reset page if filters changed
    else if (prevFilters.current !== activeFilters) {
      setActivePageNum(1)
      shouldResetPage = true
    }

    // Always fetch, but use page 1 if just reset
    const pageNum = shouldResetPage ? 1 : activePageNum
    executeBoardFetchDetails(activeTabIndex === 0, pageNum, activeFilters)

    prevTabIndex.current = activeTabIndex
    prevFilters.current = activeFilters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabIndex, activePageNum, pageSize, activeFilters, initialized])

  async function buildBoardFetchQuery(active, pageNum, filters) {
    try {
      // Set default values if needed
      active = active === undefined ? true : active
      pageNum = pageNum === undefined ? 1 : pageNum
      // Create a shallow copy of filters to avoid state mutation
      const queryFilters = filters ? { ...filters } : {}

      // Header config with token
      const config = {
        headers: {
          Authorization: `Bearer ${userCtx.tokens.getCoordinationBoardsToken()}`,
        },
      }

      // Convert filters.createdOnFilter (local JS Date) to UTC ISO string
      if (
        queryFilters.createdOnFilter &&
        queryFilters.createdOnFilter.length === 2
      ) {
        queryFilters.createdOnFilter = [
          DateTime.fromJSDate(queryFilters.createdOnFilter[0])
            .startOf("day")
            .toUTC()
            .toISO(),
          DateTime.fromJSDate(queryFilters.createdOnFilter[1])
            .endOf("day")
            .toUTC()
            .toISO(),
        ]
      }

      // Convert filters.updatedOnFilter (local JS Date) to UTC ISO string
      if (
        queryFilters.updatedOnFilter &&
        queryFilters.updatedOnFilter.length === 2
      ) {
        queryFilters.updatedOnFilter = [
          DateTime.fromJSDate(queryFilters.updatedOnFilter[0])
            .startOf("day")
            .toUTC()
            .toISO(),
          DateTime.fromJSDate(queryFilters.updatedOnFilter[1])
            .endOf("day")
            .toUTC()
            .toISO(),
        ]
      }

      // Request body (with filters, pagination & sorting)
      const body = {
        boardId: board.id,
        staffId: userCtx.session.user.staff_id,
        active: active,
        pageSize,
        pageNum: pageNum,
        createdByFilter: queryFilters.createdByFilter || [],
        createdOnFilter: queryFilters.createdOnFilter || [],
        updatedByFilter: queryFilters.updatedByFilter || [],
        updatedOnFilter: queryFilters.updatedOnFilter || [],
        tagsFilter: queryFilters.tagsFilter || [],
        searchFilter: queryFilters.searchFilter || "",
        sortByFilter: queryFilters.sortByFilter || [],
      }

      return axios.post(`${api_url}/boards/staffBoard`, body, config)
    } catch (error) {
      LogError("Unable to fetch board details", error)
      return null
    }
  }

  async function processBoardFetchDetailsResponse(boardResponse) {
    if (boardResponse.status === 200) {
      const data = await boardResponse.data
      let _topics = data.topics

      const staffIds = _topics.map((topic) => {
        let topicStaffIds = [topic.createdBy]
        if (topic.lastUpdatedBy != null) {
          topicStaffIds.push(topic.lastUpdatedBy)
        }
        if (topic.lastActivityBy != null) {
          topicStaffIds.push(topic.lastActivityBy)
        }
        return topicStaffIds
      })
      const staffNames = await getStaffsNames(staffIds.flat())

      _topics = _topics.map((topic) => {
        topic["createdByName"] = findName(topic.createdBy, staffNames)
        if (topic.lastUpdatedBy != null) {
          topic.lastUpdatedBy = findName(topic.lastUpdatedBy, staffNames)
        }
        if (topic.lastActivityBy != null) {
          topic["lastActivityByName"] = findName(
            topic.lastActivityBy,
            staffNames,
          )
        }
        return topic
      })

      setTotalTopics(data.totalTopics)

      const _active = _topics.filter((t) =>
        [TopicStatus.Active, TopicStatus.Completed].includes(t.status),
      )
      setAllActiveTopics(_active)
      if (_active.length > 0) {
        fillFilterOptions(
          data.filters,
          staffNames,
          setActiveCreatedByOptions,
          setActiveUpdatedByOptions,
          setActiveTagOptions,
        )
      }

      const _archived = _topics.filter((t) => t.status === TopicStatus.Archived)
      setAllArchivedTopics(_archived)
      if (_archived.length > 0) {
        fillFilterOptions(
          data.filters,
          staffNames,
          setArchivedCreatedByOptions,
          setArchivedUpdatedByOptions,
          setArchivedTagOptions,
        )
      }

      if (selectedTopicId) {
        const _topic = _active.find((t) => t.id === selectedTopicId)
        if (_topic) {
          // Need to set the NotOpened boolean to false
          openTopicRequest(_topic)
          setTopic(_topic)
          setShowTopicView(true)
        }
      }
    }
  }

  async function executeBoardFetchDetails(active, pageNum, filters = {}) {
    try {
      setLoadingSmall(true)
      resetTabState(!active) // Reset the state of the other (not selected) tab
      const boardResponse = await buildBoardFetchQuery(active, pageNum, filters)
      await processBoardFetchDetailsResponse(boardResponse)
    } catch (error) {
      LogError("Unable to fetch board details", error)
      const errors = error.response && error.response.data.errors
      showError(toast, "Not able to get data", errors)
    } finally {
      setLoadingSmall(false)
    }
  }

  function resetTabState(active) {
    if (active) {
      setAllActiveTopics([])
      setActiveCreatedBy([])
      setActiveCreatedOn([])
      setActiveUpdatedBy([])
      setActiveUpdatedOn([])
      setActiveTag([])
      setActiveSearchPhrase("")
      setActiveSorts(topicSortingOptions.map((o) => null))
      setActiveSortOrders(topicSortingOptions.map((o) => Ascending))
    } else {
      setAllArchivedTopics([])
      setArchivedCreatedBy([])
      setArchivedCreatedOn([])
      setArchivedUpdatedBy([])
      setArchivedUpdatedOn([])
      setArchivedTag([])
      setArchivedSearchPhrase("")
      setArchivedSorts(topicSortingOptions.map((o) => null))
      setArchivedSortOrders(topicSortingOptions.map((o) => Ascending))
    }
  }

  const openTopicRequest = (topic) => {
    const opened = topic.notOpened
    const newActivity = topic.newActivity
    topic.notOpened = false
    topic.newActivity = false

    axios
      .put(
        `${api_url}/topics/${topic.id}/open`,
        {},
        {
          headers: {
            Authorization: `Bearer ${userCtx.tokens.getCoordinationBoardsToken()}`,
          },
        },
      )
      .catch(function (response) {
        topic.notOpened = opened
        topic.newActivity = newActivity
        const errors = response["response"]["data"]["errors"]
        if (response["response"]["status"] === 400) {
          showError(toast, errors, errors)
        } else {
          showError(toast, "Error", errors)
        }
      })
  }

  const renderTopicActions = (topic) => {
    return (
      <div className={styles.table_actions} style={{ width: "70px" }}>
        {canEditTopic(topic.createdBy) ? (
          <Button
            className={styles.edit_btn}
            onClick={() => {
              setTopicAction(Actions.Edit)
              setTopic({ ...topic })
              setShowTopicForm(true)
            }}
          />
        ) : (
          //To mantain fix width
          <div style={{ width: "30px" }}> </div>
        )}
        <Button
          icon="pi pi-eye"
          className={styles.rounded_btn}
          onClick={() => {
            // Need to set the NotOpened boolean to false
            openTopicRequest(topic)
            setTopic(topic)
            setShowTopicView(true)
          }}
        />
      </div>
    )
  }

  const getTopicStatusStyle = (status) => {
    if (status === TopicStatus.Active) return styles.status_active
    if (status === TopicStatus.Completed) return styles.status_completed
    if (status === TopicStatus.Archived) return styles.status_archived
  }

  const renderNoTopics = () => {
    const headerText =
      activeTabIndex === 0
        ? "Let's start coordinating by starting the first discussion!"
        : "Nothing to show here yet. Archived topics wil appear here."
    const descText =
      activeTabIndex === 0
        ? "Starting a new coordination discussion allows you to communicate directly with partners to provide the best care for participants."
        : ""
    return (
      <div className={styles.no_record_div}>
        <NoData />
        <div style={{ width: "415px" }}>
          <p style={{ fontWeight: "700" }}>{headerText}</p>
          <p style={{ marginTop: "15px" }}>{descText}</p>
        </div>
        {activeTabIndex === 0 && (
          <Button
            className={styles.button}
            label="Start a new coordination discussion"
            disabled={board.readOnly}
            tooltip="Cannot add discussion. Board is Read-only"
            tooltipOptions={{
              disabled: !board.readOnly,
              showOnDisabled: true,
              className: "custom-tooltip",
              position: "bottom",
              at: "center bottom",
            }}
            onClick={() => {
              setTopicAction(Actions.Add)
              setTopic(null)
              setShowTopicForm(true)
            }}
          />
        )}
      </div>
    )
  }

  const renderTags = (tags, topicId) => {
    let participantTags = tags.filter((tag) => tag.type === 1)
    let otherTags = tags.filter((tag) => tag.type !== 1)
    return (
      <div key={`tags${topicId}`}>
        {otherTags.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {otherTags.map((tag, index) => {
              return (
                <Tag
                  key={`${topicId}_tag${index}`}
                  value={renderTag(tag)}
                  className={styles.tag}
                />
              )
            })}
          </div>
        )}
        {otherTags.length > 0 && participantTags.length > 0 && (
          <hr style={{ margin: "8px 0", borderColor: "#F5F5F5" }} />
        )}
        {participantTags.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {participantTags.map((tag, index) => {
              return (
                <Tag
                  key={`${topicId}_tag${index}`}
                  value={renderTag(tag)}
                  className={styles.tag}
                  style={{ backgroundColor: "#D8E6FD", color: "#0a306d87" }}
                />
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const applyFiltersAndSort = (
    createdByFilter,
    createdOnFilter,
    updatedByFilter,
    updatedOnFilter,
    tagFilter,
    searchPhrase,
    sortOptions,
    sortOrders,
  ) => {
    setActiveFilters({
      createdByFilter,
      createdOnFilter,
      updatedByFilter,
      updatedOnFilter,
      tagsFilter: tagFilter,
      searchFilter: searchPhrase,
      sortByFilter: sortOptions
        .map((sortOption, index) => ({
          fieldName: sortOption,
          ascending: sortOrders[index] === Ascending,
        }))
        .filter((so) => so.fieldName !== null),
    })
  }

  const resetFiltersAndSort = () => {
    // Active
    setActiveCreatedBy([])
    setActiveCreatedOn([])
    setActiveUpdatedBy([])
    setActiveUpdatedOn([])
    setActiveTag([])
    setActiveSearchPhrase("")
    setActiveSorts(topicSortingOptions.map((o) => null))
    setActiveSortOrders(topicSortingOptions.map((o) => Ascending))

    // Archived
    setArchivedCreatedBy([])
    setArchivedCreatedOn([])
    setArchivedUpdatedBy([])
    setArchivedUpdatedOn([])
    setArchivedTag([])
    setArchivedSearchPhrase("")
    setArchivedSorts(topicSortingOptions.map((o) => null))
    setArchivedSortOrders(topicSortingOptions.map((o) => Ascending))

    // Reset active filters state
    setActiveFilters({
      createdByFilter: [],
      createdOnFilter: [],
      updatedByFilter: [],
      updatedOnFilter: [],
      tagsFilter: [],
      searchFilter: "",
      sortByFilter: [],
    })
  }

  const renderBoardTab = (
    allTopicsList,
    createdByFilterOptions,
    createdByFilter,
    setCreatedByFilter,
    createdOnFilter,
    setCreatedOnFilter,
    updatedByFilterOptions,
    updatedByFilter,
    setUpdatedByFilter,
    updatedOnFilter,
    setUpdatedOnFilter,
    tagFilterOptions,
    tagFilter,
    setTagFilter,
    sortOptions,
    setSortOptions,
    sortOrders,
    setSortOrders,
    searchPhrase,
    setSearchPhrase,
  ) => {
    return (
      <div className={styles.flex_card}>
        <Accordion
          activeIndex={null}
          className={"custom_accordion"}
          style={{ width: "100%" }}
        >
          <AccordionTab header="Filters & Sorting">
            <div className={styles.topic_controls_first_row}>
              <div className={styles.field}>
                <label className={styles.label}>Filter by Created By</label>
                <MultiSelect
                  value={createdByFilter}
                  placeholder="All Staff"
                  onChange={(e) => setCreatedByFilter(e.value)}
                  options={createdByFilterOptions}
                  optionValue="id"
                  optionLabel="name"
                  filter
                  panelClassName="custom_multiselect_panel"
                  className={styles.selector}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Filter by Created On</label>
                <Calendar
                  value={createdOnFilter}
                  onChange={(e) => setCreatedOnFilter(e.value)}
                  selectionMode="range"
                  readOnlyInput
                  placeholder="Click to select dates"
                  panelClassName="custom_calendar_panel"
                  className={styles.calendar}
                  style={{ width: "100%" }}
                  showIcon
                  showButtonBar
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>
                  Filter by Updated/Activity By
                </label>
                <MultiSelect
                  value={updatedByFilter}
                  placeholder="All Staff"
                  onChange={(e) => {
                    setUpdatedByFilter(e.value)
                  }}
                  options={updatedByFilterOptions}
                  optionValue="id"
                  optionLabel="name"
                  filter
                  panelClassName="custom_multiselect_panel"
                  className={styles.selector}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>
                  Filter by Updated/Activity On
                </label>
                <Calendar
                  value={updatedOnFilter}
                  onChange={(e) => setUpdatedOnFilter(e.value)}
                  selectionMode="range"
                  readOnlyInput
                  placeholder="Click to select dates"
                  panelClassName="custom_calendar_panel"
                  className={styles.calendar}
                  style={{ width: "100%" }}
                  showIcon
                  showButtonBar
                />
              </div>
            </div>
            <div className={styles.topic_controls_second_row}>
              <div className={styles.field}>
                <label className={styles.label}>Filter by Tag</label>
                <MultiSelect
                  value={tagFilter}
                  placeholder="All Tags"
                  onChange={(e) => {
                    setTagFilter(e.value)
                  }}
                  options={tagFilterOptions}
                  filter
                  panelClassName="custom_multiselect_panel"
                  className={styles.selector}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Search</label>
                <InputText
                  value={searchPhrase}
                  onChange={(e) => {
                    setSearchPhrase(e.target.value)
                  }}
                  style={{ width: "100%" }}
                  name="searchPhrase"
                  placeholder="Type to search"
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Sort by</label>
                <div style={{ display: "flex", gap: "5px", width: "100%" }}>
                  <Dropdown
                    value={sortOptions[0]}
                    className={styles.selector}
                    options={topicSortingOptions}
                    panelClassName="custom_dropdown_panel"
                    onChange={(e) => {
                      setSortOptions(
                        sortOptions.map((o, index) =>
                          index === 0 ? e.value : o,
                        ),
                      )
                    }}
                    optionLabel="column"
                    optionValue="id"
                  />
                  <Button
                    icon={
                      sortOrders[0] === Ascending
                        ? "pi pi-arrow-up"
                        : "pi pi-arrow-down"
                    }
                    className={styles.sort_btn}
                    disabled={sortOptions[0] === null}
                    onClick={() => {
                      setSortOrders(
                        sortOrders.map((o, index) => (index === 0 ? !o : o)),
                      )
                    }}
                  />
                </div>
              </div>
            </div>
            <div className={styles.topic_controls_third_row}>
              {renderOtherSorts(
                sortOptions,
                setSortOptions,
                sortOrders,
                setSortOrders,
              )}
            </div>
            <div
              style={{
                marginTop: "15px",
                display: "flex",
                width: "100%",
                gap: "15px",
                justifyContent: "end",
              }}
            >
              <Button
                className={styles.cancel_button}
                label="Reset"
                style={{ height: "35px" }}
                onClick={() => {
                  resetFiltersAndSort()
                }}
              />
              <Button
                className={styles.button}
                label="Apply"
                style={{ height: "35px" }}
                onClick={() => {
                  applyFiltersAndSort(
                    createdByFilter,
                    createdOnFilter,
                    updatedByFilter,
                    updatedOnFilter,
                    tagFilter,
                    searchPhrase,
                    sortOptions,
                    sortOrders,
                  )
                }}
              />
            </div>
          </AccordionTab>
        </Accordion>
        {loadingSmall ? (
          <LoaderMedium />
        ) : (
          <>
            <div
              className={styles.rounded_wrapper}
              style={{ flexDirection: "column" }}
            >
              {allTopicsList.length === 0
                ? renderNoTopics()
                : allTopicsList.map((topic, topicIndex) => {
                    const field = mapTopicTableFields(topic)
                    const newActivity =
                      topic.newActivity &&
                      topic.lastActivityBy !== userCtx.session.user.staff_id
                    return (
                      <div
                        className={styles.topic_record}
                        id={`topic${topicIndex}`}
                        key={`topic${topicIndex}`}
                      >
                        <button
                          style={{ height: "fit-content" }}
                          onClick={() => {
                            // Need to set the NotOpened boolean to false
                            openTopicRequest(topic)
                            setTopic(topic)
                            setShowTopicView(true)
                          }}
                        >
                          <div className={styles.topic_record_header}>
                            <h3>
                              Subject{" "}
                              {topic.notOpened && (
                                <span className={styles.new_topic}>(New)</span>
                              )}
                              {newActivity && (
                                <span className={styles.new_activity}>
                                  (New Activity)
                                </span>
                              )}
                            </h3>
                            <h3>Created By</h3>
                            <h3>Created On</h3>
                            <h3>Updated/Activity By</h3>
                            <h3>Updated/Activity On</h3>
                            <h3>Tags</h3>
                          </div>
                          <div className={styles.topic_record_data}>
                            <p>{field.subject}</p>
                            <p>{field.createdBy}</p>
                            <p>{field.createdOn}</p>
                            <p>{field.updatedActivityBy}</p>
                            <p>{field.updatedActivityOn}</p>
                            {renderTags(topic.tags)}
                          </div>
                        </button>
                        <div>
                          <span
                            className={`${styles.status_chip} ${getTopicStatusStyle(topic.status)}`}
                          >
                            {field.status}
                          </span>
                        </div>
                        {renderTopicActions(topic)}
                      </div>
                    )
                  })}
            </div>
            <Paginator
              className={styles.paginator}
              first={pageSize * (activePageNum - 1)}
              rows={pageSize}
              totalRecords={totalTopics}
              rowsPerPageOptions={pageSizeOptions}
              onPageChange={(e) => {
                setPageSize(e.rows)
                setActivePageNum(e.page + 1)
              }}
            />
          </>
        )}
      </div>
    )
  }

  const addTopic = (data) => {
    if (data.createdBy != null) {
      data.createdByName = actualStaffName
    }
    const updatedRecords = [data, ...allActiveTopics]
    setAllActiveTopics(updatedRecords)
  }

  const updateTopic = (data) => {
    if (data.lastUpdatedBy != null) {
      data.lastUpdatedBy = actualStaffName
    }
    let activeRecords = [...allActiveTopics]
    let archivedRecords = [...allArchivedTopics]
    updateAndMoveTopic(activeRecords, archivedRecords, data)

    setAllActiveTopics(activeRecords)
    setAllArchivedTopics(archivedRecords)

    if (data.id === topic.id) {
      setTopic(data)
    }
  }

  const lastActivityUpdate = (data) => {
    if (data.lastActivityBy != null) {
      data.lastActivityByName = actualStaffName
    }
    const updatedRecords = allActiveTopics.map((t) => {
      if (data.id === t.id) {
        return data
      }
      return t
    })
    setAllActiveTopics(updatedRecords)

    setActiveUpdatedBy([])
    fillUpdatedByOptions(updatedRecords, setActiveUpdatedByOptions)
  }

  const followingUpdate = (data) => {
    const updatedRecords = allActiveTopics.map((t) => {
      if (data.id === t.id) {
        return { ...t, following: data.following }
      }
      return t
    })
    setAllActiveTopics(updatedRecords)

    setTopic({ ...topic, following: data.following })
  }

  if (loading) {
    return <LoaderMedium></LoaderMedium>
  }

  if (showTopicView) {
    return (
      <TopicView
        topic={topic}
        topicUpdate={updateTopic}
        lastActivityUpdate={lastActivityUpdate}
        closeView={(following) => {
          followingUpdate({ ...topic, following })
          setShowTopicView(false)
        }}
        defaultTags={defaultTags}
        groupedParticipants={groupedParticipants}
        board={board}
        canEditTopic={canEditTopic(topic.createdBy)}
        getStaffsNames={getStaffsNames}
        justStatusEdits={justStatusEdits(topic.createdBy)}
        staffSuggestions={staffSuggestions}
      />
    )
  }

  return (
    <>
      <Toast ref={toast} />
      <TopicForm
        board={board}
        addTopic={addTopic}
        updateTopic={updateTopic}
        action={topicAction}
        setDialogVisible={setShowTopicForm}
        dialogVisible={showTopicForm}
        toast={toast}
        topic={topic}
        defaultTags={defaultTags}
        groupedParticipants={groupedParticipants}
        justStatusEdits={topic ? justStatusEdits(topic.createdBy) : false}
        staffSuggestions={staffSuggestions}
      />
      <div className={styles.board}>
        <button className={styles.back_link} onClick={closeView}>
          <i className="pi pi-angle-left"></i>
          <p>All Boards</p>
        </button>
        <hr style={{ marginTop: "20px" }} />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            marginTop: "10px",
            minHeight: "50px",
          }}
        >
          <h1>{board.name}</h1>
          {allActiveTopics.length > 0 && !board.readOnly && (
            <Button
              className={styles.button}
              label="Add discussion"
              onClick={() => {
                setTopicAction(Actions.Add)
                setTopic(null)
                setShowTopicForm(true)
              }}
            />
          )}
        </div>
        <TabView
          className="custom_tabview"
          activeIndex={activeTabIndex}
          onTabChange={(e) => setActiveTabIndex(e.index)}
        >
          <TabPanel header="Active">
            {renderBoardTab(
              allActiveTopics,
              activeCreatedByOptions,
              activeCreatedBy,
              setActiveCreatedBy,
              activeCreatedOn,
              setActiveCreatedOn,
              activeUpdatedByOptions,
              activeUpdatedBy,
              setActiveUpdatedBy,
              activeUpdatedOn,
              setActiveUpdatedOn,
              activeTagOptions,
              activeTag,
              setActiveTag,
              activeSorts,
              setActiveSorts,
              activeSortOrders,
              setActiveSortOrders,
              activeSearchPhrase,
              setActiveSearchPhrase,
            )}
          </TabPanel>
          <TabPanel header="Archived">
            {renderBoardTab(
              allArchivedTopics,
              archivedCreatedByOptions,
              archivedCreatedBy,
              setArchivedCreatedBy,
              archivedCreatedOn,
              setArchivedCreatedOn,
              archivedUpdatedByOptions,
              archivedUpdatedBy,
              setArchivedUpdatedBy,
              archivedUpdatedOn,
              setArchivedUpdatedOn,
              archivedTagOptions,
              archivedTag,
              setArchivedTag,
              archivedSorts,
              setArchivedSorts,
              archivedSortOrders,
              setArchivedSortOrders,
              archivedSearchPhrase,
              setArchivedSearchPhrase,
            )}
          </TabPanel>
        </TabView>
      </div>
    </>
  )
}

export default Board
